import { useEditor } from "@craftjs/core";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { Recording } from "src/types";
import type { VideoJsPlayerOptions } from "video.js";

import { trackLibraryVideoShareClick } from "@/content-hub/analytics/video-library-events";
import {
  PlayerOverlay,
  RecordingPlayer,
} from "@/content-hub/components/video-player";
import {
  getOrganizationExternalId,
  getPageSlug,
  getPageUrl,
} from "@/content-hub/store/selectors";
import { copyContentHubInPlayerVideoShareDeepLink } from "@/content-hub/utils/copy-content-hub-browser-video-share";
import { useAppSelector } from "@/redux/store";
import togglePageScrollLock from "@/utils/toggle-page-scroll-lock";

import PlayIcon from "./assets/playIcon";
import {
  isContentHubInPlayerSharePath,
  isPublishedContentHubVideoSharePath,
} from "./is-preview-page";
import { resolveSectionIdFromElement } from "./resolve-section-id";
import {
  Caption,
  Container,
  Duration,
  PlayIconContainer,
  Thumbnail,
} from "./styles";
import { useAutoPlayFromUrl } from "./use-auto-play-from-url";
import { usePlayerToggle } from "./use-player-toggle";
import { writeVideoShareUrl } from "./write-video-share-url";

type VideoCardProps = {
  showCaption: boolean;
  recording: Recording;
  aspectRatio?: "square" | "wide";
  playerOptions?: VideoJsPlayerOptions;
  videoId?: string;
  /** Craft node id for single-video deep links; must match settings share URL `sectionId`. */
  shareSectionId?: string | null;
  /** Hub/editor overrides for caption, poster, and share `videoName`. */
  displayTitle?: string | null;
  displayThumbnailUrl?: string | null;
  /** Rich-text description below the player in preview / published views. */
  overlayDescriptionHtml?: string;
  /**
   * When true, disables query-param autoplay, in-player share, and address-bar sync
   * for this card regardless of `contentHubDirectVideoSharing` (single-video legacy).
   */
  suppressDirectVideoSharingFeatures?: boolean;
};

export const RecordingVideoCard = (props: VideoCardProps) => {
  const {
    showCaption,
    recording,
    aspectRatio = "square",
    playerOptions = {},
    videoId,
    shareSectionId,
    displayTitle: displayTitleProp,
    displayThumbnailUrl,
    overlayDescriptionHtml,
    suppressDirectVideoSharingFeatures = false,
  } = props;

  const { playableUrl, title } = recording;
  const displayTitle = (displayTitleProp ?? title) || "";
  const thumbSrc = displayThumbnailUrl || recording.thumbnail;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resolvedVideoId = videoId ?? recording.id;
  const resolvedVideoName = displayTitle;

  const playerAspectRatio =
    recording.aspectRatio === "9_16" ? "9 / 16" : undefined;

  const { isEditorEnabled } = useEditor((state) => ({
    isEditorEnabled: state.isEnabled(),
  }));

  const organizationExternalId = useAppSelector(getOrganizationExternalId);
  const pageSlug = useAppSelector(getPageSlug);
  const pageUrl = useAppSelector(getPageUrl);

  const flags = useFlags();
  const directVideoSharingEnabled =
    !suppressDirectVideoSharingFeatures &&
    Boolean(flags.contentHubDirectVideoSharing);

  const { isPlayerOpen, openPlayer, closePlayer } = usePlayerToggle({
    isEditorEnabled,
    hasPlayableUrl: !!playableUrl,
  });

  useEffect(
    function preventScrollOnPlay() {
      togglePageScrollLock({
        method: isPlayerOpen ? "add" : "remove",
        withOffset: false,
      });
    },
    [isPlayerOpen]
  );

  useAutoPlayFromUrl({
    videoId: resolvedVideoId,
    isEditorEnabled,
    containerRef,
    openPlayer,
    directVideoSharingEnabled,
    shareSectionId,
    analytics: { kind: "recording", recordingId: resolvedVideoId },
  });

  const canUseInPlayerShare =
    typeof window !== "undefined" &&
    directVideoSharingEnabled &&
    isContentHubInPlayerSharePath(window.location.pathname);

  const syncAddressBarQueryOnPlay =
    typeof window !== "undefined" &&
    directVideoSharingEnabled &&
    !isEditorEnabled &&
    isPublishedContentHubVideoSharePath(window.location.pathname);

  const handleShare = async () => {
    if (!canUseInPlayerShare || typeof window === "undefined") return;

    trackLibraryVideoShareClick({
      kind: "recording",
      recordingId: resolvedVideoId,
    });
    await copyContentHubInPlayerVideoShareDeepLink({
      organizationExternalId,
      pageSlug,
      pageUrl,
      getVideoCardElement: () => containerRef.current,
      videoId: resolvedVideoId,
      videoName: resolvedVideoName || null,
    });
  };

  const handlePlay = () => {
    if (syncAddressBarQueryOnPlay) {
      const sectionId =
        shareSectionId?.trim() ||
        resolveSectionIdFromElement(containerRef.current);

      if (resolvedVideoId && sectionId) {
        writeVideoShareUrl({
          videoId: resolvedVideoId,
          sectionId,
          videoName: resolvedVideoName,
          locationTag: "content-hub/recording-video-card:url-update",
        });
      }
    }

    openPlayer();
  };

  return (
    <>
      <Container ref={containerRef} $enabled={!isEditorEnabled}>
        <Thumbnail
          $aspectRatio={aspectRatio}
          alt={displayTitle}
          src={thumbSrc}
          onClick={handlePlay}
        />
        <DurationTag duration={recording.duration} />
        {showCaption && <Caption>{displayTitle}</Caption>}
        <PlayIconContainer>
          <span className="sr-only">Play video</span>
          {!isPlayerOpen && <PlayIcon />}
        </PlayIconContainer>
      </Container>
      {isPlayerOpen &&
        createPortal(
          <PlayerOverlay
            title={displayTitle}
            onClose={closePlayer}
            aspectRatio={playerAspectRatio}
            onShareClick={canUseInPlayerShare ? handleShare : undefined}
            descriptionHtml={overlayDescriptionHtml}
          >
            <RecordingPlayer recording={recording} {...playerOptions} />
          </PlayerOverlay>,
          document.body
        )}
    </>
  );
};

export const DurationTag = ({ duration }: { duration: number }) => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;

  const hourText = hours > 0 ? `${hours}:` : "";
  const minutesText = minutes < 10 ? `0${minutes}` : minutes;
  const secondsText = seconds < 10 ? `0${seconds}` : seconds;

  return <Duration>{`${hourText}${minutesText}:${secondsText}`}</Duration>;
};
